import React from 'react';
import {
  Box, Switch, Typography, Tooltip, IconButton, Grid, Dialog, DialogTitle, DialogContent, DialogActions, Button, Alert, AlertTitle
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { useTranslation } from 'react-i18next';
import ActionConditionsSection from './ActionConditionsSection';
import ActionStatusWindow from './Actions/ActionStatusWindow';
import TagsDropdown from './TagsDropdown';

/**
 * Complete unified action settings layout
 * Contains everything except the action-specific form fields
 */
const ActionSettingsLayout = ({
  // State
  isEnabled,
  onEnabledChange,
  title,
  isSaveDisabled,
  onSave,
  onForceRun,
  forceRunLoading,
  primaryColor = '#5757b0',
  
  // Conditions
  conditions,
  onConditionsChange,
  tags,
  enableConditions = true,
  
  // Status window
  popUpOpen,
  forceRunResult,
  onCloseStatusWindow,

  // Force run warning
  forceRunWarningOpen = false,
  forceRunWarningCount = 0,
  onForceRunConfirm = null,
  onForceRunCancel = null,
  allowForceRunWithUnsavedChanges = false,
  
  // Tags dropdown (if needed by action)
  tagsDropdownProps = null,
  
  // Action-specific content (rendered between conditions and bottom)
  children
  }) => {
  const { t } = useTranslation();
  const forceRunDisabled = forceRunLoading || (!allowForceRunWithUnsavedChanges && !isSaveDisabled);
  const forceRunTooltip = allowForceRunWithUnsavedChanges
    ? (forceRunLoading
        ? t('action_common.running_test', "Running test...")
        : t('action_common.force_run', 'Test Run'))
    : (!isSaveDisabled
        ? t('action_common.save_first', "Save changes first to enable test run")
        : forceRunLoading
          ? t('action_common.running_test', "Running test...")
          : t('action_common.force_run', 'Test Run'));

  return (
    <Box sx={{
      p: 0,
      maxWidth: { xs: '100%', lg: 1000, xl: 1200 },
      mx: 'auto',
      backgroundColor: 'transparent',
    }}>
      <Box sx={{
        overflow: 'hidden',
        borderBottom: isEnabled ? `1px solid ${primaryColor}20` : '1px solid #f0f0f5',
      }}>
        {/* Header with toggle, title, and action buttons */}
        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'flex-start', sm: 'center' },
          gap: { xs: 1.5, sm: 0 },
          p: { xs: 1.5, sm: 2 },
          background: isEnabled
            ? `linear-gradient(145deg, ${primaryColor}08 0%, ${primaryColor}04 100%)`
            : 'linear-gradient(145deg, #f5f5f7 0%, #eeeef0 100%)',
          borderBottom: isEnabled ? `1px solid ${primaryColor}20` : 'none',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', width: { xs: '100%', sm: 'auto' } }}>
            <Switch
              checked={isEnabled}
              onChange={(e) => onEnabledChange(e.target.checked)}
              color="primary"
              size="small"
              sx={{
                '& .MuiSwitch-switchBase': {
                  '&.Mui-checked': {
                    color: primaryColor,
                    '& + .MuiSwitch-track': {
                      backgroundColor: primaryColor,
                      opacity: 0.8,
                    },
                  },
                },
                '& .MuiSwitch-thumb': {
                  boxShadow: '0 2px 4px 0 rgba(0,0,0,0.2)',
                },
                '& .MuiSwitch-track': {
                  borderRadius: 16,
                  opacity: 0.3,
                },
              }}
            />
            <Typography variant="h6" sx={{
              ml: 1.5,
              fontWeight: 700,
              color: isEnabled ? primaryColor : '#666',
              transition: 'color 0.3s ease',
              letterSpacing: '-0.2px',
              fontSize: { xs: '0.95rem', sm: '1.05rem', md: '1.15rem' },
              flexGrow: { xs: 1, sm: 0 }
            }}>
              {title}
            </Typography>
          </Box>
          <Box sx={{ flexGrow: 1 }} />
          <Box sx={{ display: 'flex', justifyContent: { xs: 'flex-end', sm: 'flex-start' }, width: { xs: '100%', sm: 'auto' }, mt: { xs: 1, sm: 0 } }}>
            <Tooltip title={t('action_common.save_settings', 'Save Settings')}>
              <span>
                <IconButton 
                  onClick={onSave} 
                  disabled={isSaveDisabled} 
                  sx={{
                    color: isSaveDisabled ? '#c0c0d0' : primaryColor,
                    '&:hover': {
                      backgroundColor: `${primaryColor}10`,
                    },
                  }}
                >
                  <SaveIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={forceRunTooltip}>
              <span>
                <IconButton 
                  onClick={onForceRun} 
                  disabled={forceRunDisabled}
                  sx={{
                    color: primaryColor,
                    '&:hover': {
                      backgroundColor: `${primaryColor}10`,
                    },
                    '&.Mui-disabled': {
                      color: '#c0c0d0',
                    },
                  }}
                >
                  <PlayArrowIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Box>

        {/* Content area - always visible so settings can be reviewed before enabling the action */}
        <Box sx={{ 
          p: { xs: 2, md: 3 }, 
          backgroundColor: 'transparent',
          opacity: isEnabled ? 1 : 0.92,
        }}>
          {!isEnabled && (
            <Alert
              severity="info"
              variant="outlined"
              sx={{
                mb: 2,
                borderColor: `${primaryColor}30`,
                backgroundColor: `${primaryColor}08`,
                color: '#555',
                borderRadius: '10px',
                '& .MuiAlert-icon': {
                  color: primaryColor,
                },
              }}
            >
              <AlertTitle sx={{ color: primaryColor, fontWeight: 700, mb: 0.25 }}>
                {t('action_common.disabled_notice_title', 'Action disabled')}
              </AlertTitle>
              {t(
                'action_common.disabled_notice_body',
                'You can still review and edit the settings before enabling this action.'
              )}
            </Alert>
          )}
          <Grid container spacing={ { xs: 2, md: 3 } }>
            {/* Conditions editor and action-specific fields stay accessible even when disabled */}
            {enableConditions && (
              <ActionConditionsSection
                conditions={conditions}
                onConditionsChange={onConditionsChange}
                tags={tags}
              />
            )}

            {/* Action-specific content */}
            {children}
          </Grid>

          {/* Bottom Save Button */}
          <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end', pt: 3, borderTop: '1px solid #f0f0f5' }}>
            <Button
              variant="contained"
              onClick={onSave}
              disabled={isSaveDisabled}
              startIcon={<SaveIcon />}
              sx={{
                backgroundColor: primaryColor,
                color: 'white',
                px: 4,
                py: 1,
                borderRadius: '10px',
                boxShadow: `0 4px 14px ${primaryColor}40`,
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 600,
                '&:hover': {
                  backgroundColor: primaryColor,
                  filter: 'brightness(1.1)',
                  boxShadow: `0 6px 20px ${primaryColor}60`,
                },
                '&.Mui-disabled': {
                  backgroundColor: '#e0e0e0',
                  color: '#a0a0a0',
                  boxShadow: 'none',
                }
              }}
            >
              {t('action_common.save_settings', 'Save Settings')}
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Status Window */}
      <ActionStatusWindow
        isOpen={popUpOpen}
        onClose={onCloseStatusWindow}
        results={forceRunResult}
      />

      <Dialog
        open={forceRunWarningOpen}
        onClose={onForceRunCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {t('action_common.force_run_warning_title', 'Large Test Run')}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ mt: 1 }}>
            {t(
              'action_common.force_run_warning_body',
              `This test will run on ${forceRunWarningCount} events. Running on a large number of events can take time.`
            )}
          </Typography>
          <Typography sx={{ mt: 1 }}>
            {t(
              'action_common.force_run_warning_choice',
              'Would you like to test only the first event instead?'
            )}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onForceRunCancel}>
            {t('action_common.cancel', 'Cancel')}
          </Button>
          <Button
            variant="outlined"
            onClick={() => onForceRunConfirm && onForceRunConfirm('first_event_only')}
          >
            {t('action_common.force_run_first_event', 'Test First Event Only')}
          </Button>
          <Button
            variant="contained"
            onClick={() => onForceRunConfirm && onForceRunConfirm('all_events')}
          >
            {t('action_common.force_run_all_events', 'Run All Events')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Optional Tags Dropdown */}
      {tagsDropdownProps && (
        <TagsDropdown {...tagsDropdownProps} />
      )}
    </Box>
  );
};

export default ActionSettingsLayout;
